import './DataDictionaryTable.scss';

import classNames from 'classnames';
import { DataDictionaryBulkEditModal } from './DataDictionaryBulkEditModal';
import { DataDictionaryQueryParams } from './DataDictionary';
import { getClosestSelectionTable } from '@lib/popups';
import { getSonarObjectTypeLabelPlural } from '@sonar-software/sonar-ts-lib';
import { KeyPagination } from '@ui/KeyPagination';
import { SelectionTable } from '@ui/tables';

import { DataDictionaryEntry, DataDictionaryResponse, DataDictionarySchema } from '@sonar-software/sonar-core';
import {
	DynamicFilter,
	KeyPaginationQueryParams,
	PaginatedResource,
	PaginationDirection,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import {
	parseMetadataFromResponse,
	translateFilterQueryParamsToDynamicQueryLanguage,
	translateOrderQueryParamsToDynamicQueryLanguage
} from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TableFilterQueryParams, TableModifier } from '@ui/table-modifier';
import {
	UpdateQueryParams,
	useFeatureFlags,
	useOnUnmount,
	usePeriscopeActions,
	usePeriscopeCount,
	usePrevious,
	useSelectionTable,
	UseSelectionTableResult
} from '@hooks';

const convertToDql = (queryParams: TableFilterQueryParams) => {
	const { filterCount, sortBy, sortDefault, sortDirection, ...rest } = queryParams;

	return {
		filters: translateFilterQueryParamsToDynamicQueryLanguage(rest as DynamicFilter),
		ordering: translateOrderQueryParamsToDynamicQueryLanguage(queryParams),
		paginationDirection: PaginationDirection.Forward
	};
};

export interface DataDictionaryTableProps {
	loading: boolean;
	queryParams: DataDictionaryQueryParams;
	resource: DataDictionaryResponse | null | undefined;
	updateQuery: UpdateQueryParams<DataDictionaryQueryParams>;
}

type Resource = PaginatedResource<DataDictionaryEntry, DataDictionarySchema>;
export type DataDictionaryTableQueryParams = KeyPaginationQueryParams;

function getResourceIdsUri(queryParams: DataDictionaryQueryParams) {
	switch (true) {
		case Boolean(queryParams.applicationId):
			return `v3/data-dictionary/applications/${queryParams.applicationId}/bulk`;
		case Boolean(queryParams.tagId):
			return `v3/data-dictionary/tags/${queryParams.tagId}/bulk`;
		case Boolean(queryParams.sonarObjectId):
			return `v3/data-dictionary/fields/${queryParams.sonarObjectId}/bulk`;
		case Boolean(queryParams.sonarObjectType):
			return `v3/data-dictionary/sonar-objects/types/${queryParams.sonarObjectType}/bulk`;
		case typeof queryParams.complianceCategory === 'number':
			return `v3/data-dictionary/compliance-categories/${queryParams.complianceCategory}/bulk`;
		default:
			return '';
	}
}

function getRowKey(entry: DataDictionaryEntry) {
	return entry.sonarObjectId;
}

export const DataDictionaryTable: React.FC<DataDictionaryTableProps> = ({
	loading,
	queryParams,
	resource,
	updateQuery
}) => {
	const [ showEditModal, setShowEditModal ] = useState(false);
	const editButtonRef = useRef<HTMLElement | null>(null);

	const { openPeriscope } = usePeriscopeActions();
	const periscopeCount = usePeriscopeCount();

	const { isEnabled } = useFeatureFlags();

	const sonarObjectTypeOrField = queryParams.sonarObjectType ?? SonarObjectType.SalesforceField;

	const previousSonarObjectId = usePrevious(queryParams.sonarObjectId);
	const previousSonarObjectType = usePrevious(queryParams.sonarObjectType);
	const tableDataChanged =
		queryParams.sonarObjectId !== previousSonarObjectId || queryParams.sonarObjectType !== previousSonarObjectType;

	const scrollableListRef = useRef<HTMLDivElement>(null);
	const selectionDataRef = useRef<UseSelectionTableResult<DataDictionaryEntry> | null>(null);

	const handleRowClick = useCallback(
		(record: DataDictionaryEntry) => ({
			onClick: () => {
				const { name, sonarObjectId: recordSonarObjectId } = record;

				openPeriscope({
					id: recordSonarObjectId,
					name: name,
					openedFromIndex: -1,
					type: sonarObjectTypeOrField
				});
			}
		}),
		[ openPeriscope, sonarObjectTypeOrField ]
	);

	const { columns, filters, schema, sortOptions } = useMemo(
		() => parseMetadataFromResponse(resource?.schema, isEnabled, queryParams.columns, getClosestSelectionTable),
		[ isEnabled, queryParams.columns, resource ]
	);

	selectionDataRef.current = useSelectionTable<DataDictionaryEntry>({
		getRowKey,
		namespace: 'dataDictionary',
		queryParams: queryParams as Record<string, unknown>,
		resourceIdsUri: getResourceIdsUri(queryParams),
		results: resource?.results,
		selectionType: sonarObjectTypeOrField
	});

	useLayoutEffect(() => {
		const header = scrollableListRef.current?.getElementsByClassName('ant-table-header')[ 0 ];

		if (header) {
			const height = header.firstElementChild?.getBoundingClientRect().height;

			if (height) {
				(header as HTMLElement).style.minHeight = `${height - 1}px`;
			}
		}
	});

	useOnUnmount(() => {
		selectionDataRef.current?.resetSelection();
	});

	useEffect(() => {
		if (selectionDataRef.current?.totalCount && tableDataChanged) {
			selectionDataRef.current?.resetSelection();
		}
	}, [ queryParams, selectionDataRef, tableDataChanged ]);

	const handleEditModalOpen = useCallback((button: HTMLElement) => {
		editButtonRef.current = button;
		setShowEditModal(true);
	}, []);

	const handleEditModalCancel = useCallback(() => {
		setShowEditModal(false);
	}, []);

	const handleEditModalSuccess = useCallback(() => {
		selectionDataRef.current?.resetSelection();
		setShowEditModal(false);
	}, [ selectionDataRef ]);

	const canClick = queryParams.sonarObjectType !== SonarObjectType.SalesforceProfile;

	const dataDictionaryTableClassnames = classNames('data-dictionary-table', {
		'no-click': !canClick,
		'periscope-open': periscopeCount === 1
	});

	return (
		<>
			{ Boolean(resource) &&
				<TableModifier
					allowColumnConfig
					columns={ columns }
					columnsString={ queryParams.columns }
					convertToDynamicQueryLanguage={ convertToDql }
					filterDisplayType='menu'
					filters={ filters }
					filtersString={ queryParams.filters }
					onChange={ updateQuery }
					orderString={ queryParams.ordering }
					sonarObjectId={ queryParams.sonarObjectId }
					sonarObjectType={ queryParams.sonarObjectType }
					sortOptions={ sortOptions } /> }
			<SelectionTable
				alwaysShowCount
				className={ dataDictionaryTableClassnames }
				columns={ schema }
				dataSource={ resource?.results }
				forceShowSelection={ canClick ? undefined : false }
				loading={ loading }
				onEditSelection={ handleEditModalOpen }
				onRow={ canClick ? handleRowClick : undefined }
				ref={ scrollableListRef }
				rowKey={ getRowKey }
				scroll={ {
					scrollToFirstRowOnChange: true,
					x: resource?.results.length ? 'max-content' : 4000
				} }
				selectionData={ selectionDataRef.current }
				selectionType={ getSonarObjectTypeLabelPlural(sonarObjectTypeOrField) } />
			<KeyPagination
				defaultPageSize={ Number(queryParams.count) }
				listRef={ scrollableListRef }
				onChange={ updateQuery }
				resource={ resource as Resource } />
			<DataDictionaryBulkEditModal
				onCancel={ handleEditModalCancel }
				onOk={ handleEditModalSuccess }
				open={ showEditModal }
				popoverTarget={ editButtonRef }
				selected={ selectionDataRef.current?.selectedKeys }
				sonarObjectType={ sonarObjectTypeOrField } />
		</>
	);
};
